import React, { useState, useEffect, useMemo } from 'react';
import { CheckCircle, X, Clock, Trophy, Target } from './Icons';
import { useSupabaseData } from '../hooks/useSupabaseData';

const TestMode = ({ 
  words, 
  language = 'traditional', 
  onComplete,
  timeLimit = 300 // 5 minutes default
}) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState('');
  const [userAnswers, setUserAnswers] = useState([]);
  const [timeLeft, setTimeLeft] = useState(timeLimit);
  const [testCompleted, setTestCompleted] = useState(false);
  const [showResults, setShowResults] = useState(false);

  // Timer
  useEffect(() => {
    if (timeLeft > 0 && !testCompleted) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0 && !testCompleted) {
      handleTestComplete();
    }
  }, [timeLeft, testCompleted]);

  // If words not provided, load a set from Supabase
  const { data: remote } = useSupabaseData({
    table: 'eng_vocabulary',
    mapRow: (row) => ({
      id: row.word_id || row.id,
      english: row.english,
      traditional: row.traditional || row.zh_traditional,
      simplified: row.simplified || row.zh_simplified,
      pronunciation: row.pronunciation || row.phonetic
    }),
    getFallback: () => null,
    query: { limit: 12 }
  });

  const effectiveWords = useMemo(() => {
    if (Array.isArray(words) && words.length > 0) return words;
    if (Array.isArray(remote) && remote.length > 0) return remote;
    return [];
  }, [words, remote]);

  const currentWord = effectiveWords[currentQuestionIndex];
  
  const getQuestionData = (word) => {
    const options = [];
    const correctAnswer = language === 'traditional' ? word.traditional :
                         language === 'simplified' ? word.simplified :
                         word.english;
    
    options.push(correctAnswer);
    
    // Add 3 random incorrect options from other words
    const otherWords = effectiveWords.filter(w => w.id !== word.id);
    const shuffledOthers = otherWords.sort(() => 0.5 - Math.random()).slice(0, 3);
    
    shuffledOthers.forEach(w => {
      const wrongAnswer = language === 'traditional' ? w.traditional :
                         language === 'simplified' ? w.simplified :
                         w.english;
      options.push(wrongAnswer);
    });

    return {
      question: language === 'english' ? word.traditional : word.english,
      correctAnswer,
      options: options.sort(() => 0.5 - Math.random())
    };
  };

  const questionData = currentWord ? getQuestionData(currentWord) : null;

  const handleAnswerSelect = (answer) => {
    setSelectedAnswer(answer);
  };

  const handleNextQuestion = () => {
    const newAnswer = {
      questionId: currentWord.id,
      question: questionData.question,
      selectedAnswer,
      correctAnswer: questionData.correctAnswer,
      isCorrect: selectedAnswer === questionData.correctAnswer
    };

    setUserAnswers([...userAnswers, newAnswer]);

    if (currentQuestionIndex < words.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setSelectedAnswer('');
    } else {
      handleTestComplete();
    }
  };

  const handleTestComplete = () => {
    setTestCompleted(true);
    setShowResults(true);
  };

  const calculateScore = () => {
    const correctAnswers = userAnswers.filter(answer => answer.isCorrect).length;
    return Math.round((correctAnswers / userAnswers.length) * 100);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getScoreColor = (score) => {
    if (score >= 90) return 'text-green-400';
    if (score >= 70) return 'text-yellow-400';
    return 'text-red-400';
  };

  if (showResults) {
    const score = calculateScore();
    const timeTaken = timeLimit - timeLeft;

    return (
      <div className="p-6 text-center space-y-6">
        {/* Results Header */}
        <div className="space-y-4">
          <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto ${
            score >= 70 ? 'bg-green-500' : 'bg-red-500'
          }`}>
            {score >= 70 ? (
              <Trophy className="w-10 h-10 text-white" />
            ) : (
              <Target className="w-10 h-10 text-white" />
            )}
          </div>
          
          <h2 className="text-2xl font-bold">
            {language === 'traditional' ? '測試完成！' :
             language === 'simplified' ? '测试完成！' :
             'Test Complete!'}
          </h2>
        </div>

        {/* Score */}
        <div className="bg-white/10 rounded-xl p-6">
          <div className={`text-4xl font-bold mb-2 ${getScoreColor(score)}`}>
            {score}%
          </div>
          <p className="text-gray-300">
            {userAnswers.filter(a => a.isCorrect).length} / {userAnswers.length} 
            {language === 'traditional' ? ' 題正確' :
             language === 'simplified' ? ' 题正确' :
             ' correct'}
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white/10 rounded-xl p-4">
            <Clock className="w-6 h-6 mx-auto mb-2 text-blue-400" />
            <div className="font-bold">{formatTime(timeTaken)}</div>
            <div className="text-sm text-gray-300">
              {language === 'traditional' ? '用時' :
               language === 'simplified' ? '用时' :
               'Time Used'}
            </div>
          </div>
          <div className="bg-white/10 rounded-xl p-4">
            <Target className="w-6 h-6 mx-auto mb-2 text-purple-400" />
            <div className="font-bold">{Math.round((userAnswers.length / timeTaken) * 60)}</div>
            <div className="text-sm text-gray-300">
              {language === 'traditional' ? '題/分鐘' :
               language === 'simplified' ? '题/分钟' :
               'Q/min'}
            </div>
          </div>
        </div>

        {/* Review Answers */}
        <div className="space-y-3 max-h-60 overflow-y-auto">
          <h3 className="font-semibold">
            {language === 'traditional' ? '答題回顧' :
             language === 'simplified' ? '答题回顾' :
             'Answer Review'}
          </h3>
          {userAnswers.map((answer, index) => (
            <div key={index} className={`p-3 rounded-lg text-left ${
              answer.isCorrect ? 'bg-green-500/20' : 'bg-red-500/20'
            }`}>
              <div className="flex items-center space-x-2 mb-1">
                {answer.isCorrect ? (
                  <CheckCircle className="w-4 h-4 text-green-400" />
                ) : (
                  <X className="w-4 h-4 text-red-400" />
                )}
                <span className="font-medium">Q{index + 1}</span>
              </div>
              <p className="text-sm text-gray-300 mb-1">{answer.question}</p>
              <p className="text-sm">
                <span className="text-gray-400">
                  {language === 'traditional' ? '你的答案：' :
                   language === 'simplified' ? '你的答案：' :
                   'Your answer: '}
                </span>
                <span className={answer.isCorrect ? 'text-green-400' : 'text-red-400'}>
                  {answer.selectedAnswer}
                </span>
              </p>
              {!answer.isCorrect && (
                <p className="text-sm">
                  <span className="text-gray-400">
                    {language === 'traditional' ? '正確答案：' :
                     language === 'simplified' ? '正确答案：' :
                     'Correct answer: '}
                  </span>
                  <span className="text-green-400">{answer.correctAnswer}</span>
                </p>
              )}
            </div>
          ))}
        </div>

        {/* Actions */}
        <div className="flex space-x-3">
          <button 
            onClick={() => window.location.reload()}
            className="flex-1 py-3 bg-blue-500 rounded-lg"
          >
            {language === 'traditional' ? '重新測試' :
             language === 'simplified' ? '重新测试' :
             'Retake Test'}
          </button>
          <button 
            onClick={() => onComplete && onComplete(score, timeTaken)}
            className="flex-1 py-3 bg-green-500 rounded-lg"
          >
            {language === 'traditional' ? '完成' :
             language === 'simplified' ? '完成' :
             'Finish'}
          </button>
        </div>
      </div>
    );
  }

  if (!questionData) {
    return (
      <div className="p-6 text-center">
        <p className="text-gray-400">
          {language === 'traditional' ? '載入中...' :
           language === 'simplified' ? '加载中...' :
           'Loading...'}
        </p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Test Header */}
      <div className="flex justify-between items-center">
        <div className="text-sm text-gray-400">
          {language === 'traditional' ? '測試模式' :
           language === 'simplified' ? '测试模式' :
           'Test Mode'}
        </div>
        <div className={`flex items-center space-x-2 ${
          timeLeft < 60 ? 'text-red-400' : 'text-blue-400'
        }`}>
          <Clock className="w-4 h-4" />
          <span className="font-mono">{formatTime(timeLeft)}</span>
        </div>
      </div>

      {/* Progress */}
      <div>
        <div className="flex justify-between text-sm text-gray-400 mb-2">
          <span>
            {language === 'traditional' ? '進度' :
             language === 'simplified' ? '进度' :
             'Progress'}
          </span>
          <span>{currentQuestionIndex + 1} / {effectiveWords.length}</span>
        </div>
        <div className="w-full bg-gray-700 rounded-full h-2">
          <div 
            className="bg-blue-500 h-2 rounded-full transition-all duration-300"
            style={{ width: `${effectiveWords.length ? (((currentQuestionIndex + 1) / effectiveWords.length) * 100) : 0}%` }}
          ></div>
        </div>
      </div>

      {/* Question */}
      <div className="bg-white/10 rounded-xl p-6 text-center">
        <h2 className="text-lg font-semibold mb-4">
          {language === 'traditional' ? '這個詞的意思是什麼？' :
           language === 'simplified' ? '这个词的意思是什么？' :
           'What does this word mean?'}
        </h2>
        <div className="text-2xl font-bold text-blue-400 mb-4">
          {questionData.question}
        </div>
        {currentWord && currentWord.image && (
          <div className="w-full h-32 rounded-lg overflow-hidden mb-4">
            <img 
              src={currentWord.image} 
              alt={currentWord.english}
              className="w-full h-full object-cover"
            />
          </div>
        )}
      </div>

      {/* Options */}
      <div className="space-y-3">
        {questionData.options.map((option, index) => (
          <button
            key={index}
            onClick={() => handleAnswerSelect(option)}
            className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
              selectedAnswer === option
                ? 'border-blue-400 bg-blue-500/20'
                : 'border-gray-600 bg-gray-800/30 hover:border-gray-500'
            }`}
          >
            <div className="flex items-center space-x-3">
              <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                selectedAnswer === option
                  ? 'border-blue-400 bg-blue-400'
                  : 'border-gray-500'
              }`}>
                {selectedAnswer === option && (
                  <div className="w-2 h-2 bg-white rounded-full"></div>
                )}
              </div>
              <span className="font-medium">{option}</span>
            </div>
          </button>
        ))}
      </div>

      {/* Next Button */}
      <button
        onClick={handleNextQuestion}
        disabled={!selectedAnswer}
        className="w-full py-4 bg-green-500 rounded-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {currentQuestionIndex === effectiveWords.length - 1 ? 
          (language === 'traditional' ? '完成測試' :
           language === 'simplified' ? '完成测试' :
           'Finish Test') :
          (language === 'traditional' ? '下一題' :
           language === 'simplified' ? '下一题' :
           'Next Question')
        }
      </button>
    </div>
  );
};

export default TestMode;
